<template>
  <!-- Teleport so toasts always render above whatever route is active,
       including cinematic full-bleed players. Bottom-right on desktop,
       full-width bottom-center on small screens (so the touch target
       isn't cramped against the gutter). -->
  <Teleport to="body">
    <div class="toast-host" :class="{ 'toast-host--tv': isTvMode }" aria-live="polite" aria-atomic="false">
      <TransitionGroup name="toast" tag="div" class="toast-host__stack">
        <div
          v-for="t in toast.toasts.value"
          :key="t.id"
          class="toast"
          :class="`toast--${t.kind}`"
          role="status"
        >
          <span class="toast__icon" aria-hidden="true">
            <!-- Inline SVGs so we don't depend on an icon set being loaded.
                 Each is sized to match the 16px text leading. -->
            <svg v-if="t.kind === 'success'" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <svg v-else-if="t.kind === 'error'" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
            </svg>
            <svg v-else viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-.01V10a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
            </svg>
          </span>
          <span class="toast__message">{{ t.message }}</span>
          <button
            v-if="t.action"
            type="button"
            class="toast__action"
            @click="onAction(t)"
          >{{ t.action.label }}</button>
          <button
            type="button"
            class="toast__dismiss"
            aria-label="Dismiss"
            @click="toast.dismiss(t.id)"
          >
            <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { useUiStore } from '@/stores/ui'
import { useToast } from '@/composables/useToast'

const toast = useToast()
const ui = useUiStore()
const isTvMode = computed(() => ui.tvMode)

function onAction(t) {
  // Fire the action callback first so the caller can decide whether to keep
  // the toast on screen (e.g. "Undo" might want to leave the toast up while
  // it runs). Default behavior: dismiss after the click so the host isn't
  // littered with completed actions.
  try { t.action.onClick() } catch (err) { console.warn('[toast] action threw:', err) }
  toast.dismiss(t.id)
}
</script>

<style scoped>
.toast-host {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 80;
  pointer-events: none;
  display: flex;
  justify-content: flex-end;
}
@media (max-width: 639px) {
  .toast-host {
    left: 1rem;
    right: 1rem;
    justify-content: stretch;
  }
}
/* TV mode: pin to bottom-center, scale up sizes so toasts are legible
   across the room. */
.toast-host--tv {
  left: 50%;
  right: auto;
  bottom: 3rem;
  transform: translateX(-50%);
  font-size: 1.1rem;
}
.toast-host__stack {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 22rem;
}
.toast-host--tv .toast-host__stack { max-width: 32rem; }

.toast {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.9rem;
  border-radius: 0.75rem;
  background: rgb(17 24 39 / 0.95);
  color: #f9fafb;
  box-shadow: 0 10px 25px -10px rgb(0 0 0 / 0.5);
  font-size: 0.9rem;
  line-height: 1.3;
  backdrop-filter: blur(6px);
  border: 1px solid rgb(255 255 255 / 0.08);
}
.toast--success { background: rgb(6 78 59 / 0.95); border-color: rgb(16 185 129 / 0.4); }
.toast--error   { background: rgb(127 29 29 / 0.95); border-color: rgb(248 113 113 / 0.4); }
.toast--info    { background: rgb(30 58 138 / 0.95); border-color: rgb(96 165 250 / 0.4); }

.toast__icon { flex-shrink: 0; opacity: 0.9; display: inline-flex; }
.toast__message { flex: 1; min-width: 0; word-break: break-word; }
.toast__action {
  flex-shrink: 0;
  padding: 0.25rem 0.6rem;
  border-radius: 0.4rem;
  background: rgb(255 255 255 / 0.15);
  color: inherit;
  font-weight: 600;
  font-size: 0.85em;
}
.toast__action:hover { background: rgb(255 255 255 / 0.25); }
.toast__dismiss {
  flex-shrink: 0;
  opacity: 0.6;
  padding: 0.25rem;
  color: inherit;
}
.toast__dismiss:hover { opacity: 1; }

/* Slide-in from the right, fade-out in place. The leave-active phase
   removes the toast from layout so siblings reflow up immediately. */
.toast-enter-from { opacity: 0; transform: translateY(8px) translateX(20px); }
.toast-enter-active, .toast-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}
.toast-leave-to { opacity: 0; transform: translateX(20px); }
.toast-leave-active { position: absolute; }
</style>
